import React, { useState, useEffect } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';

import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';

// ── Color tokens ──────────────────────────────────────────────────────────────
const GN  = '#0e7490';
const GNL = '#ecfeff';
const GND = '#0c4a6e';

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp2  = { border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '12px', height: '30px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', color: '#1a2e2e', cursor: 'default' };
const inp2R = { ...inp2, textAlign: 'right' };
const lbl2  = { fontSize: '10px', color: '#6b7280', display: 'block', marginBottom: '2px', fontWeight: 500 };
const sectionBox   = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden' };
const sectionHead  = { backgroundColor: GNL, borderBottom: `2px solid ${GN}`, padding: '5px 12px' };
const sectionTitle = { fontSize: '11px', fontWeight: 700, color: GND, textTransform: 'uppercase', letterSpacing: '0.5px' };

const StockTransferDetails = () => {
    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, token, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    }, [isAuthenticated]);

    const [formData, setFormData]       = useState({});
    const [selectedItems, setSelectedItems] = useState([]);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    useEffect(() => {
        fetchData('StockTransferApi', `/${id}`, token)
            .then(response => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.StockTransfer);
                    setSelectedItems(response.response_data.StockTransferDetails);
                }
            })
            .catch(err => console.error('Error fetching Stock Transfer Details:', err));
    }, [id]);

    return (
        <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: '#f5f5f5' }}>

            {/* ── Top action bar ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '13px', color: GN }}>Stock Transfer Details</span>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Link to="/StockTransferIndex"
                        style={{ height: '30px', padding: '0 14px', borderRadius: '4px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', fontWeight: 600, fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                        ← Back
                    </Link>
                </div>
            </div>

            {/* ══ SECTION 1 — Transfer Information ══ */}
            <div style={sectionBox}>
                <div style={sectionHead}><span style={sectionTitle}>Transfer Information</span></div>
                <div style={{ padding: '8px 12px', display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>

                    {/* Bill No */}
                    <div style={{ flex: '0 0 110px' }}>
                        <label style={lbl2}>Bill No</label>
                        <input type="text" value={`${formData.BillNumber || ''}/${formData.FinYear || ''}`} readOnly style={inp2} />
                    </div>

                    {/* Bill Date */}
                    <div style={{ flex: '0 0 140px' }}>
                        <label style={lbl2}>Bill Date</label>
                        <input type="date" value={formatDateForInput(formData.BillDate)} readOnly style={inp2} />
                    </div>

                    {/* From Store Location */}
                    <div style={{ flex: '1 1 180px' }}>
                        <label style={lbl2}>From Store Location</label>
                        <input type="text" value={formData.FromStoreLocationName || ''} readOnly style={inp2} />
                    </div>

                    {/* To Store Location */}
                    <div style={{ flex: '1 1 180px' }}>
                        <label style={lbl2}>To Store Location</label>
                        <input type="text" value={formData.ToStoreLocationName || ''} readOnly style={inp2} />
                    </div>

                    {/* Total Qty */}
                    <div style={{ flex: '0 0 100px' }}>
                        <label style={lbl2}>Total Qty</label>
                        <input type="text" value={formData.TotalQuantity || 0} readOnly style={{ ...inp2R, fontWeight: 700, color: GND }} />
                    </div>

                    {/* Created By / Date */}
                    <div style={{ flex: '0 0 140px' }}>
                        <label style={lbl2}>Created Date</label>
                        <input type="text" value={formData.CreatedDate ? new Date(formData.CreatedDate).toLocaleDateString('en-GB') : ''} readOnly style={inp2} />
                    </div>

                </div>
            </div>

            {/* ══ SECTION 2 — Transfer Items ══ */}
            <div style={sectionBox}>
                <div style={{ ...sectionHead, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={sectionTitle}>Transfer Items ({selectedItems.length} items)</span>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedItems.length === 0 ? (
                        <div style={{ backgroundColor: GNL, padding: '20px', textAlign: 'center', fontSize: '12px', color: GND }}>No items found</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead style={{ backgroundColor: GN, color: '#fff', position: 'sticky', top: 0 }}>
                                <tr>
                                    {['#', 'Item', 'Quantity'].map((h, i) => (
                                        <th key={i} style={{ padding: '6px 8px', textAlign: i > 1 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedItems.map((item, index) => (
                                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : GNL, borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '3px 8px', textAlign: 'center', width: '50px', color: '#6b7280' }}>{index + 1}</td>
                                        <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.ItemName}</td>
                                        <td style={{ padding: '2px', width: '100px' }}>
                                            <input type="number" value={item.Quantity} readOnly style={{ ...inp2R, height: '24px', borderRadius: '4px' }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr style={{ backgroundColor: GNL, borderTop: `2px solid ${GN}` }}>
                                    <td colSpan={2} style={{ padding: '5px 8px', fontWeight: 700, fontSize: '11px', color: GND, textAlign: 'right' }}>Total Quantity:</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, fontSize: '12px', color: GN }}>
                                        {selectedItems.reduce((s, i) => s + parseFloat(i.Quantity || 0), 0)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    )}
                </div>
            </div>

        </div>
    );
};

export default StockTransferDetails;
